import { HttpApiService } from "@/shared/services/httpApi.service";
import { TokenStorageService } from "@/shared/services/tokenStorage.service";

import { AUTH_API_ENDPOINTS } from "../constants/auth.constants";
import type { ApiLoginResponse, ApiMeResponse } from "../types/auth-api-response.interface";
import { turnstileHeaders } from "../utils/turnstile";

export const authService = {
  login: async (
    email: string,
    password: string,
    twoFactorCode?: string,
    turnstileToken?: string | null,
  ): Promise<ApiLoginResponse> => {
    const response = await HttpApiService.post<ApiLoginResponse>(
      AUTH_API_ENDPOINTS.LOGIN,
      { email, password, twoFactorCode },
      { headers: turnstileHeaders(turnstileToken) },
    );
    return response.data;
  },

  getMe: async (): Promise<ApiMeResponse> => {
    const response = await HttpApiService.get<ApiMeResponse>(AUTH_API_ENDPOINTS.GET_ME);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await HttpApiService.post(AUTH_API_ENDPOINTS.LOGOUT, {});
  },

  // Silent renewal on 401 is handled by the httpApi interceptor; this wrapper
  // is for any explicit keep-alive use.
  refresh: async (): Promise<{ access_token?: string; refresh_token?: string }> => {
    const response = await HttpApiService.post<{ access_token?: string; refresh_token?: string }>(
      AUTH_API_ENDPOINTS.REFRESH,
      { refresh_token: TokenStorageService.getRefreshToken() ?? undefined },
    );
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await HttpApiService.post(AUTH_API_ENDPOINTS.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  },

  requestPasswordReset: async (email: string, turnstileToken?: string | null): Promise<void> => {
    await HttpApiService.post(
      AUTH_API_ENDPOINTS.FORGOT_PASSWORD,
      { email },
      { headers: turnstileHeaders(turnstileToken) },
    );
  },

  verifyResetCode: async (email: string, code: string): Promise<void> => {
    await HttpApiService.post(AUTH_API_ENDPOINTS.VERIFY_RESET_CODE, {
      email,
      code,
    });
  },

  resetPassword: async (payload: {
    email: string;
    code?: string;
    token?: string;
    newPassword: string;
  }): Promise<void> => {
    await HttpApiService.post(AUTH_API_ENDPOINTS.RESET_PASSWORD, payload);
  },
};
